
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function About() {
  const leadership = [
    {
      name: 'Jaya Krishna M',
      position: 'Managing Director',
      experience: '15+ years',
      expertise: 'Business Strategy & Operations',
      image: '/images/team/team-member-2.jpg',
    },
    {
      name: 'Priya Sharma',
      position: 'Technical Director',
      experience: '12+ years',
      expertise: 'Equipment Engineering & Design',
      image: '/images/team/team-member-1.png',
    },
    {
      name: 'Amit Patel',
      position: 'Sales Director',
      experience: '10+ years',
      expertise: 'Client Relations & Market Development',
      image: '/images/team/team-member-1.png',
    },
  ];

  const values = [
    {
      icon: 'ri-award-line',
      title: 'Quality First',
      description:
        'We partner only with manufacturers who meet our rigorous quality standards, ensuring every piece of equipment delivers exceptional performance and reliability.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Customer-Centric',
      description:
        'Our customers are at the heart of everything we do. We listen, understand, and deliver solutions that exceed expectations.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description:
        'Transparency and honesty guide our business practices. We build long‑term relationships based on trust and mutual respect.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description:
        'We stay ahead of industry trends, bringing the latest technology and innovative solutions to our customers.',
    },
    {
      icon: 'ri-star-line',
      title: 'Service Excellence',
      description:
        'From consultation to after‑sales support, we maintain the highest standards of service quality and responsiveness.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability',
      description:
        'We promote eco‑friendly solutions and energy‑efficient equipment that helps businesses reduce their environmental impact.',
    },
  ];

  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description:
        'GalaxyFabTech Pvt Ltd established with a vision to transform the commercial laundry industry in India.',
    },
    {
      year: '2021',
      title: 'First Partnerships',
      description:
        'Secured partnerships with Wonder Clean India and Electrolux Professional, expanding our product portfolio.',
    },
    {
      year: '2022',
      title: 'Market Expansion',
      description:
        'Extended services to healthcare and hospitality sectors, establishing strong presence in key markets.',
    },
    {
      year: '2023',
      title: 'Technology Integration',
      description:
        'Implemented advanced customer service systems and launched comprehensive training programs.',
    },
    {
      year: '2024',
      title: 'Strategic Growth',
      description:
        'Added Stefab India and Alliance Laundry Systems to our partner network, serving 500+ customers.',
    },
    {
      year: '2025',
      title: 'Future Vision',
      // The ₹ sign is escaped using Unicode to avoid JSX parsing errors.
      description:
        'Targeting \u20B95Cr revenue with 30% growth, becoming India\'s most trusted laundry equipment trader.',
    },
  ];

  const stats = [
    { number: '25+', label: 'Years Combined Experience' },
    { number: '500+', label: 'Happy Customers' },
    { number: '4', label: 'Brand Partners' },
    { number: '6', label: 'Industry Segments' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-building-4-line text-sm text-white"></i>
                </div>
                About Our Company
                <span className="w-2 h-2 bg-blue-500 rounded-full ml-3"></span>
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
              About{' '}
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                GalaxyFabTech
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Building the future of commercial laundry solutions in India through quality equipment,
              trusted partnerships, and exceptional service.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">25+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600">500+</div>
                <div className="text-sm text-gray-600">Happy Customers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">4</div>
                <div className="text-sm text-gray-600">Brand Partners</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Founded in 2020, GalaxyFabTech Pvt Ltd emerged from a simple yet powerful vision:
                  to bridge the gap between world‑class laundry equipment manufacturers and businesses
                  across India that needed reliable, efficient solutions.
                </p>
                <p>
                  What started as a small trading company has grown into a trusted partner for
                  hundreds of businesses across hospitality, healthcare, education, and industrial
                  sectors. Our success is built on three pillars: quality products, exceptional
                  service, and lasting relationships.
                </p>
                <p>
                  Today, we&apos;re proud to represent some of the world&apos;s leading laundry equipment
                  manufacturers and continue our mission to empower businesses with the tools they need for
                  operational excellence.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="primary" size="lg">
                    <i className="ri-chat-1-line mr-2"></i>
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="/images/about/laundry-facility.jpg"
                alt="Our Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="text-2xl font-bold text-blue-600">500+</div>
                <div className="text-gray-600">Happy Customers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50 relative overflow-hidden">
        {/* Background Elements */}
        <div
          className="absolute inset-0 bg-[url('/images/about/mission-bg.jpg')] bg-cover bg-center opacity-5"
        ></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200/30 rounded-full blur-xl animate-bounce"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-teal-200/40 rounded-full blur-lg animate-pulse"></div>
        <div
          className="absolute bottom-20 left-1/4 w-12 h-12 bg-purple-200/30 rounded-full blur-lg animate-bounce"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute top-1/2 right-10 w-8 h-8 bg-blue-300/40 rounded-full blur-md animate-pulse"
          style={{ animationDelay: '2s' }}
        ></div>

        {/* Floating Icons */}
        <div className="absolute top-40 left-1/3 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center animate-float">
          <i className="ri-lightbulb-line text-blue-500/60 text-lg"></i>
        </div>
        <div
          className="absolute bottom-32 right-1/3 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center animate-float"
          style={{ animationDelay: '1.5s' }}
        >
          <i className="ri-rocket-line text-teal-500/60 text-lg"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-blue-100/80 to-teal-100/80 backdrop-blur-sm border border-white/30 text-blue-800 text-sm font-semibold shadow-lg">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-compass-3-line text-sm text-white"></i>
                </div>
                Our Purpose &amp; Direction
                <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full ml-3"></span>
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-800 via-blue-700 to-teal-700 bg-clip-text text-transparent mb-6 leading-tight">
              Mission &amp; Vision
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Driving transformation through excellence, innovation, and unwavering commitment to our
              customers
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Mission Card */}
            <div className="group cursor-pointer">
              <Card
                className="p-10 h-full bg-white/20 backdrop-blur-xl border border-white/30 shadow-2xl hover:shadow-3xl hover:-translate-y-3 hover:bg-white/30 transition-all duration-500 relative overflow-hidden"
                hover={false}
              >
                {/* Card Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Floating Icon */}
                <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-600/30 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <i className="ri-focus-3-line text-blue-600 text-2xl"></i>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-6 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all duration-500">
                      <i className="ri-focus-3-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-800 group-hover:text-blue-700 transition-colors duration-300">
                        Mission
                      </h3>
                      <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mt-2 group-hover:w-24 transition-all duration-500"></div>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                    To empower businesses with the right equipment and service for operational excellence,
                    connecting world‑class manufacturers with businesses needing comprehensive laundry solutions
                    that drive efficiency, sustainability, and growth.
                  </p>

                  {/* Mission Highlights */}
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    <div className="flex items-center text-sm text-blue-600 font-medium">
                      <i className="ri-check-line mr-2 text-teal-500"></i>
                      Quality Equipment
                    </div>
                    <div className="flex items-center text-sm text-blue-600 font-medium">
                      <i className="ri-check-line mr-2 text-teal-500"></i>
                      Expert Service
                    </div>
                    <div className="flex items-center text-sm text-blue-600 font-medium">
                      <i className="ri-check-line mr-2 text-teal-500"></i>
                      Trusted Partnerships
                    </div>
                    <div className="flex items-center text-sm text-blue-600 font-medium">
                      <i className="ri-check-line mr-2 text-teal-500"></i>
                      Business Growth
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Vision Card */}
            <div className="group cursor-pointer">
              <Card
                className="p-10 h-full bg-white/20 backdrop-blur-xl border border-white/30 shadow-2xl hover:shadow-3xl hover:-translate-y-3 hover:bg-white/30 transition-all duration-500 relative overflow-hidden"
                hover={false}
              >
                {/* Card Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-teal-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Floating Icon */}
                <div className="absolute top-6 right-6 w-16 h-16 bg-gradient-to-br from-teal-500/20 to-teal-600/30 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <i className="ri-eye-line text-teal-600 text-2xl"></i>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mr-6 shadow-xl group-hover:shadow-2xl group-hover:scale-110 transition-all duration-500">
                      <i className="ri-eye-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-800 group-hover:text-teal-700 transition-colors duration-300">
                        Vision
                      </h3>
                      <div className="w-16 h-1 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full mt-2 group-hover:w-24 transition-all duration-500"></div>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                    To become India&apos;s most trusted commercial laundry equipment trader,
                    renowned for innovative solutions, exceptional service support, and our commitment to
                    transforming businesses across every industry sector.
                  </p>

                  {/* Vision Goals */}
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    <div className="flex items-center text-sm text-teal-600 font-medium">
                      <i className="ri-star-line mr-2 text-blue-500"></i>
                      Market Leadership
                    </div>
                    <div className="flex items-center text-sm text-teal-600 font-medium">
                      <i className="ri-star-line mr-2 text-blue-500"></i>
                      Innovation Focus
                    </div>
                    <div className="flex items-center text-sm text-teal-600 font-medium">
                      <i className="ri-star-line mr-2 text-blue-500"></i>
                      Service Excellence
                    </div>
                    <div className="flex items-center text-sm text-teal-600 font-medium">
                      <i className="ri-star-line mr-2 text-blue-500"></i>
                      Industry Impact
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="group cursor-pointer">
                <Card
                  className="p-8 text-center bg-white/15 backdrop-blur-xl border border-white/25 shadow-xl hover:shadow-2xl hover:-translate-y-2 hover:bg-white/25 transition-all duration-500 relative overflow-hidden"
                  hover={false}
                >
                  {/* Background Glow */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div className="relative z-10">
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 text-sm font-medium group-hover:text-gray-700 transition-colors duration-300">
                      {stat.label}
                    </div>

                    {/* Floating Icon */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-400/20 to-teal-400/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110">
                      <i className="ri-arrow-up-line text-blue-500 text-sm"></i>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%,
            100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(5deg);
            }
          }
          .animate-float {
            animation: animate-float 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Leadership Team */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Leadership Team</h2>
            <p className="text-xl text-gray-600">
              Experienced professionals driving our company's growth and success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leadership.map((leader, index) => (
              <Card key={index} className="text-center overflow-hidden">
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{leader.name}</h3>
                  <p className="text-blue-600 font-medium mb-2">{leader.position}</p>
                  <p className="text-sm text-gray-600 mb-2">{leader.experience}</p>
                  <p className="text-sm text-gray-500">{leader.expertise}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Core Values */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Elements */}
        <div
          className="absolute inset-0 bg-[url('/images/about/values-bg.jpg')] bg-cover bg-center opacity-5"
        ></div>

        {/* Floating Background Elements */}
        <div className="absolute top-10 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-teal-200/25 rounded-full blur-xl animate-bounce"></div>
        <div
          className="absolute bottom-32 left-1/4 w-20 h-20 bg-purple-200/20 rounded-full blur-lg animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute bottom-20 right-20 w-16 h-16 bg-blue-300/30 rounded-full blur-md animate-bounce"
          style={{ animationDelay: '2s' }}
        ></div>

        {/* Floating 3D Icons */}
        <div className="absolute top-1/3 left-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-lg">
          <i className="ri-heart-line text-blue-500/70 text-xl"></i>
        </div>
        <div
          className="absolute top-1/2 right-16 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-lg"
          style={{ animationDelay: '1.5s' }}
        >
          <i className="ri-diamond-line text-teal-500/70 text-xl"></i>
        </div>
        <div
          className="absolute bottom-1/3 left-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-lg"
          style={{ animationDelay: '3s' }}
        >
          <i className="ri-star-line text-purple-500/70 text-xl"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <div className="mb-8">
              <span className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-blue-100/80 to-teal-100/80 backdrop-blur-xl border border-white/40 text-blue-800 text-sm font-bold shadow-xl">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center mr-4 shadow-lg">
                  <i className="ri-heart-3-line text-sm text-white"></i>
                </div>
                Core Values &amp; Principles
                <span className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full ml-4 animate-pulse"></span>
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-800 via-blue-700 to-teal-700 bg-clip-text text-transparent mb-8 leading-tight">
              Our Core Values
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The fundamental principles that guide our business excellence and shape every interaction
            </p>

            {/* Decorative Line */}
            <div className="mt-12 flex justify-center">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {values.map((value, index) => (
              <div key={index} className="group cursor-pointer">
                <Card
                  className="p-8 h-full bg-white/15 backdrop-blur-2xl border border-white/30 shadow-2xl hover:shadow-3xl hover:-translate-y-4 hover:bg-white/25 transition-all duration-500 relative overflow-hidden transform hover:scale-105"
                  hover={false}
                >
                  {/* Card Background Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Floating Particle Effects */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-blue-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div
                    className="absolute bottom-2 left-2 w-1 h-1 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700"
                    style={{ animationDelay: '0.5s' }}
                  ></div>

                  {/* 3D Icon Container */}
                  <div className="relative mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-teal-600 rounded-3xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-teal-700 rounded-3xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
                      <i className={`${value.icon} text-white text-3xl group-hover:scale-110 transition-transform duration-300`}></i>

                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/50 to-teal-400/50 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                    </div>

                    {/* Floating Ring */}
                    <div className="absolute inset-0 w-20 h-20 border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                  </div>

                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-700 transition-colors duration-300">
                      {value.title}
                    </h3>

                    {/* Animated Underline */}
                    <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mb-6 group-hover:w-20 transition-all duration-500"></div>

                    <p className="text-gray-600 leading-relaxed text-lg group-hover:text-gray-700 transition-colors duration-300">
                      {value.description}
                    </p>

                    {/* Interactive Progress Bar */}
                    <div className="mt-8 h-2 bg-gray-200/50 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-teal-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-6 right-6 w-10 h-10 bg-gradient-to-br from-blue-500/20 to-teal-500/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Reflection Effect */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Card>
              </div>
            ))}
          </div>

          {/* Enhanced Stats Row */}
          <div className="mt-20 text-center">
            <div className="inline-flex items-center space-x-12 bg-white/10 backdrop-blur-2xl border border-white/30 rounded-3xl px-12 py-8 shadow-2xl">
              <div className="text-center group cursor-pointer">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  6
                </div>
                <div className="text-gray-600 text-sm font-medium group-hover:text-gray-700 transition-colors duration-300">
                  Core Values
                </div>
              </div>
              <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              <div className="text-center group cursor-pointer">
                <div className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-purple-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  100%
                </div>
                <div className="text-gray-600 text-sm font-medium group-hover:text-gray-700 transition-colors duration-300">
                  Commitment
                </div>
              </div>
              <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              <div className="text-center group cursor-pointer">
                <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  500+
                </div>
                <div className="text-gray-600 text-sm font-medium group-hover:text-gray-700 transition-colors:duration-300">
                  Happy Clients
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Floating Animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%,
            100% {
              transform: translateY(0px) rotate(0deg);
              opacity: 0.7;
            }
            25% {
              transform: translateY(-8px) rotate(2deg);
              opacity: 1;
            }
            50% {
              transform: translateY(-15px) rotate(-1deg);
              opacity: 0.8;
            }
            75% {
              transform: translateY(-5px) rotate(3deg);
              opacity: 1;
            }
          }
          .animate-float {
            animation: animate-float 4s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Elements */}
        <div
          className="absolute inset-0 bg-[url('/images/about/cta-bg.jpg')] bg-cover bg-center opacity-5"
        ></div>

        {/* Floating Background Elements */}
        <div className="absolute top-16 left-20 w-28 h-28 bg-blue-200/20 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-32 right-32 w-32 h-32 bg-teal-200/25 rounded-full blur-xl animate-bounce"></div>
        <div
          className="absolute bottom-20 left-1/4 w-24 h-24 bg-purple-200/20 rounded-full blur-lg animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute bottom-32 right-20 w-20 h-20 bg-blue-300/30 rounded-full blur-md animate-bounce"
          style={{ animationDelay: '2s' }}
        ></div>

        {/* Floating 3D Icons */}
        <div className="absolute top-1/4 left-16 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-xl">
          <i className="ri-handshake-line text-blue-500/70 text-2xl"></i>
        </div>
        <div
          className="absolute top-1/2 right-20 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-xl"
          style={{ animationDelay: '1.5s' }}
        >
          <i className="ri-rocket-line text-teal-500/70 text-2xl"></i>
        </div>
        <div
          className="absolute bottom-1/3 left-1/2 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-float shadow-xl"
          style={{ animationDelay: '3s' }}
        >
          <i className="ri-phone-line text-purple-500/70 text-2xl"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Main CTA Card */}
            <div className="group cursor-pointer">
              <Card
                className="p-12 bg-white/15 backdrop-blur-2xl border border-white/30 shadow-2xl hover:shadow-3xl hover:-translate-y-3 hover:bg-white/25 transition-all duration-500 relative overflow-hidden"
                hover={false}
              >
                {/* Card Background Glow */}
                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Floating Particle Effects */}
                <div className="absolute top-4 right-4 w-3 h-3 bg-blue-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                <div
                  className="absolute bottom-4 left-4 w-2 h-2 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700"
                  style={{ animationDelay: '0.5s' }}
                ></div>

                <div className="relative z-10">
                  {/* Main Heading */}
                  <div className="text-center mb-8">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                      Ready to Partner with Us?
                    </h2>

                    {/* Decorative Line */}
                    <div className="flex justify-center mb-6">
                      <div className="w-24 h-1 bg-blue-500 rounded-full"></div>
                    </div>

                    <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-medium">
                      Join hundreds of satisfied customers who trust GalaxyFabTech for their commercial laundry
                      needs. Let's build success together.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
                    <Link to="/contact">
                      <div className="group/btn relative">
                        {/* 3D Button Effect */}
                        <div className="absolute inset-0 bg-blue-700 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                        <button className="relative bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                          <span className="flex items-center">
                            <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                              <i className="ri-phone-line text-sm"></i>
                            </div>
                            Contact Us Today
                          </span>
                        </button>
                      </div>
                    </Link>

                    <Link to="/services">
                      <div className="group/btn relative">
                        {/* 3D Button Effect */}
                        <div className="absolute inset-0 bg-gray-300 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                        <button className="relative bg-white/80 backdrop-blur-xl border-2 border-gray-300 hover:bg-white hover:border-gray-400 text-gray-800 hover:text-gray-900 font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap">
                          <span className="flex items-center">
                            <div className="w-6 h-6 bg-gray-200/60 rounded-lg flex items-center justify-center mr-3">
                              <i className="ri-service-line text-sm"></i>
                            </div>
                            Explore Services
                          </span>
                        </button>
                      </div>
                    </Link>
                  </div>

                  {/* Statistics Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                    {/* Stat 1 */}
                    <div className="text-center group/stat cursor-pointer">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 relative">
                          <div className="absolute inset-0 bg-white/10 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                          <i className="ri-user-heart-line text-blue-600 text-3xl group-hover:scale-110 transition-transform duration-300"></i>
                          <div className="absolute inset-0 w-20 h-20 border-2 border-blue-300/30 rounded-3xl group-hover:scale-125 group-hover:border-blue-400/40 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-blue-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                        500+
                      </div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider group-hover:text-gray-800 transition-colors duration-300">
                        Happy Customers
                      </div>
                    </div>

                    {/* Stat 2 */}
                    <div className="text-center group/stat cursor-pointer">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 relative">
                          <div className="absolute inset-0 bg-white/10 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                          <i className="ri-time-line text-teal-600 text-3xl group-hover:scale-110 transition-transform duration-300"></i>
                          <div className="absolute inset-0 w-20 h-20 border-2 border-teal-300/30 rounded-3xl group-hover:scale-125 group-hover:border-teal-400/40 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-teal-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                        24/7
                      </div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider group-hover:text-gray-800 transition-colors duration-300">
                        Support Available
                      </div>
                    </div>

                    {/* Stat 3 */}
                    <div className="text-center group/stat cursor-pointer">
                      <div className="relative mb-4">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 relative">
                          <div className="absolute inset-0 bg-white/10 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                          <i className="ri-award-line text-purple-600 text-3xl group-hover:scale-110 transition-transform duration-300"></i>
                          <div className="absolute inset-0 w-20 h-20 border-2 border-purple-300/30 rounded-3xl group-hover:scale-125 group-hover:border-purple-400/40 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-purple-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                        25+
                      </div>
                      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wider group-hover:text-gray-800 transition-colors duration-300">
                        Years Experience
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reflection Effect */}
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Card>
            </div>
          </div>
        </div>

        {/* Enhanced Floating Animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%,
            100% {
              transform: translateY(0px) rotate(0deg);
              opacity: 0.7;
            }
            25% {
              transform: translateY(-10px) rotate(3deg);
              opacity: 1;
            }
            50% {
              transform: translateY(-18px) rotate(-2deg);
              opacity: 0.8;
            }
            75% {
              transform: translateY(-8px) rotate(4deg);
              opacity: 1;
            }
          }
          .animate-float {
            animation: animate-float 4s ease-in-out infinite;
          }
        `}</style>
      </section>

      <Footer />
    </div>
  );
}
